/**
 * Product tour content. Bump TOUR_VERSION when the steps change enough that
 * everyone should see the tour again (it auto-plays once per account per version).
 */
export const TOUR_VERSION = '2026-11';

export interface TourStep {
  id: string;
  /** `[data-tour="…"]` to spotlight; omitted = centered card. */
  target?: string;
  title: string;
  body: string[];
  /** Only for companies where the email agent is switched on. */
  needsAgent?: boolean;
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to the new RiskNinja',
    body: [
      "A lot has changed. Here's a two-minute tour of the new pieces.",
      'You can replay it any time from the Support page.',
    ],
  },
  {
    id: 'tools',
    title: 'One tool per line of business',
    body: [
      'Inside any customer folder, Run Workflow now shows one tool for each line: Cyber, D&O, Home Insurance, Commercial Lines, Loss Runs and more.',
      'Drop in the documents and say what you need. RiskNinja picks the right analysis for you, whether that is a benchmark, a renewal comparison or a quote comparison.',
      'The earlier tools are still there under Legacy.',
      'Want to pick an exact analysis yourself? Switch Workflow view to Dev in your account menu to see every individual tool.',
    ],
  },
  {
    id: 'arie-chat',
    target: 'arie-chat',
    title: 'Meet Arie',
    body: [
      'Arie is your in-app assistant. Click the ninja to ask a coverage question, find a document, file new documents or start an analysis.',
      'Anything that changes your data shows up as a card for you to confirm first.',
    ],
  },
  {
    id: 'email-arie',
    title: 'Email Arie your work',
    needsAgent: true,
    body: [
      'Send or forward an email to your company\'s Arie mailbox from the address on your account, and say what you want above the forwarded message, e.g. "run the year-over-year" or "fill in the attached application".',
      'Arie files the attachments, runs the work and emails you a summary with the reports and filled templates attached. Reply to that email to keep going.',
      'Arie never emails your clients. Any reply she writes for them is saved as a draft for you to send.',
    ],
  },
  {
    id: 'arie-menu',
    target: 'arie-menu',
    title: "Arie's activity and approvals",
    needsAgent: true,
    body: [
      'Everything Arie does is listed under Agent activity, step by step.',
      'When a step needs your OK, it waits under Approvals. The badge shows how many are waiting. Your Arie address and settings are here too.',
    ],
  },
  {
    id: 'folder-actions',
    title: 'Run Arie on a folder',
    body: [
      'Each customer folder has a Run Arie button at the top.',
      'Type what you want done with that folder, such as "compare the two quotes" or "draft a renewal summary for the client", and Arie works through it in the background.',
    ],
  },
  {
    id: 'library-menu',
    target: 'library-menu',
    title: 'Your library',
    body: ['Templates, your agency Knowledge Base and Insights now live together under Library.'],
  },
  {
    id: 'notifications',
    target: 'notifications',
    title: 'Notifications',
    body: ['The bell tells you when a report is ready or Arie needs you, in one list.'],
  },
  {
    id: 'recent-activity',
    target: 'recent-activity',
    title: 'Recent activity',
    body: ['Jump back to the customers, folders and reports you opened recently.'],
  },
  {
    id: 'user-menu',
    target: 'user-menu',
    title: 'Your account',
    body: ['Settings, password and your forwarding addresses for emailing Arie are in your account menu.'],
  },
  {
    id: 'done',
    title: "You're all set",
    body: [
      'Open Support any time to replay this tour or read the user guide for Arie, emailing Arie and the tools.',
      'Questions? Ask Arie, or open a support ticket.',
    ],
  },
];
